import { useSetState } from '@domain-group/react-hooks'

import { Button, Form, SidebarModal } from '@domain-group/suit-theme'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useReporting from '@hooks/useReporting'

const MODAL_KEY = 'GenerateReportModal'

type DefaultState = {
  end_date?: string,
  reportKey: 'template_rejections' | 'first_time_right_leaderboard' | '',
  start_date?: string,
}

const defaultState: DefaultState = {
  end_date: '',
  reportKey: '',
  start_date: '',
}

type ModalCallbacks = {
  closeModal: () => void,
  generateTeamPerformanceReport: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const GenerateReportModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, generateTeamPerformanceReport } = callbacks

  const [state, setState] = useSetState(defaultState)
  const { end_date, start_date, reportKey } = state

  const reportingPayload = useReporting({
    allowedReports: reportKey ? [reportKey] : [],
    defaultValues: {
      chart_data_key: reportKey || 'first_time_right_leaderboard',
      end_date: end_date || undefined,
      start_date: start_date || undefined,
    },
    formDeps: [reportKey, end_date, start_date],
  })

  const {
    callbacks: { generateReport }, loading, saveEnabled,
  } = reportingPayload

  const generateReportPayload = {
    callbacks: {
      action: generateReport,
      afterAction: () => closeModal(),
    },
    toastText: 'Report Queued',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title="Team Performance" titleSecondLine="Report" />

      <SidebarModal.Content>
        <Form.Field label="* Report Type">
          <select
            name="report_type"
            value={reportKey}
            onChange={e => setState({ reportKey: e.target.value })}
          >
            <option value="">Please Select...</option>
            <option value="first_time_right_leaderboard">Task Accuracy Leaderboard</option>
            <option value="template_rejections">Template Rejections by Client QC</option>
          </select>
        </Form.Field>

        <Form.Row>
          <Form.Field label="Start Date" marginTop="large">
            <input
              name="start_date"
              onChange={e => setState({ start_date: e.target.value })}
              type="date"
              value={start_date}
            />
          </Form.Field>

          <Form.Field label="End Date" marginTop="large">
            <input
              disabled={!start_date}
              name="end_date"
              onChange={e => setState({ end_date: e.target.value })}
              type="date"
              value={end_date}
              min={start_date}
            />
          </Form.Field>
        </Form.Row>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={loading}
          loadingBubbleColor="white"
          onClick={() => generateTeamPerformanceReport(generateReportPayload)}
          size="large"
        >
          {saveEnabled ? 'Download Report' : 'Complete Fields'}
        </Button>

        <Button buttonStyle="secondaryUtility" onClick={() => closeModal()} size="large">
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <GenerateReportModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
